import React, { useState } from 'react';
import { colors, typography, spacing } from '../styles/design-system';

interface HockeyDataDisplayProps {
  data: any;
  homeTeam: string;
  awayTeam: string;
}

const HockeyDataDisplay: React.FC<HockeyDataDisplayProps> = ({ data, homeTeam, awayTeam }) => {
  const [selectedTeam, setSelectedTeam] = useState<'home' | 'away'>('home');

  // 수신 데이터 디버그 로그
  React.useEffect(() => {
    console.log('HockeyDataDisplay 수신 데이터:', data);
    console.log('데이터 키:', Object.keys(data || {}));
    console.log('선발 골텐더:', data?.starting_goalies);
    console.log('팀 통계:', data?.team_stats);
    console.log('상대전적:', data?.head_to_head);
  }, [data]);

  // 데이터 섹션 추출
  const startingGoalies = data.starting_goalies || {};
  const headToHead = data.head_to_head || {};
  const teamStats = data.team_stats || {};
  const periodScores = data.period_scores || data.match_summary?.period_scores || {};
  const recentForm = data.recent_form || {};
  const dateTime = data.date_time;
  const venue = data?.venue;

  // 테이블 렌더링
  const renderTable = (tableData: any, title?: string): React.ReactElement | null => {
    if (!tableData) return null;

    let headers: string[] = [];
    let rows: any[][] = [];

    // 다양한 데이터 형식 처리
    if (tableData.headers && tableData.data) {
      headers = tableData.headers;
      rows = tableData.data;
    } else if (Array.isArray(tableData) && tableData.length > 0) {
      if (Array.isArray(tableData[0])) {
        headers = tableData[0];
        rows = tableData.slice(1);
      }
    } else if (tableData.data && Array.isArray(tableData.data)) {
      if (tableData.data[0] && Array.isArray(tableData.data[0])) {
        headers = tableData.data[0];
        rows = tableData.data.slice(1);
      }
    }

    if (headers.length === 0 || rows.length === 0) return null;

    return (
      <div style={styles.tableContainer}>
        {title && <h4 style={styles.tableTitle}>{title}</h4>}
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                {headers.map((header, idx) => (
                  <th key={idx} style={styles.th}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row: any[], rowIdx: number) => (
                <tr key={rowIdx}>
                  {row.map((cell, cellIdx) => (
                    <td key={cellIdx} style={styles.td}>{cell || '-'}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // 골텐더 정보 렌더링
  const renderGoalieInfo = (goalie: any, teamName: string, icon: string): React.ReactElement | null => {
    if (!goalie || !goalie.name) return null;

    const record = goalie.record || {};
    const gaa = goalie.gaa || 0;
    const svPct = goalie.sv_pct || 0;

    return (
      <div style={styles.goalieCard}>
        <h4 style={styles.goalieHeader}>
          {icon} {teamName} - #{goalie.number || ''} {goalie.name}
        </h4>
        <div style={styles.goalieStats}>
          <div style={styles.statItem}>
            <span style={styles.statLabel}>승</span>
            <span style={styles.statValue}>{record.wins ?? '-'}</span>
          </div>
          <div style={styles.statItem}>
            <span style={styles.statLabel}>패</span>
            <span style={styles.statValue}>{record.losses ?? '-'}</span>
          </div>
          <div style={styles.statItem}>
            <span style={styles.statLabel}>OTL</span>
            <span style={styles.statValue}>{record.otl ?? '-'}</span>
          </div>
          <div style={styles.statItem}>
            <span style={styles.statLabel}>GAA</span>
            <span style={styles.statValue}>{gaa ? gaa.toFixed(2) : '-'}</span>
          </div>
          <div style={styles.statItem}>
            <span style={styles.statLabel}>SV%</span>
            <span style={styles.statValue}>{svPct ? (svPct < 1 ? svPct.toFixed(3) : (svPct / 100).toFixed(3)) : '-'}</span>
          </div>
        </div>
      </div>
    );
  };

  // 피리어드 점수 렌더링
  const renderPeriodScores = (): React.ReactElement | null => {
    if (!periodScores || (!periodScores.home && !periodScores.away)) return null;

    const homeScores = periodScores.home || {};
    const awayScores = periodScores.away || {};

    const headers = ['팀', '1P', '2P', '3P'];
    if (homeScores.ot || awayScores.ot) headers.push('OT');
    if (homeScores.so || awayScores.so) headers.push('SO');
    headers.push('합계');

    const homeRow = [homeTeam];
    const awayRow = [awayTeam];

    ['p1', 'p2', 'p3'].forEach(period => {
      homeRow.push(String(homeScores[period] ?? 0));
      awayRow.push(String(awayScores[period] ?? 0));
    });

    if (headers.includes('OT')) {
      homeRow.push(String(homeScores.ot ?? 0));
      awayRow.push(String(awayScores.ot ?? 0));
    }
    if (headers.includes('SO')) {
      homeRow.push(String(homeScores.so ?? 0));
      awayRow.push(String(awayScores.so ?? 0));
    }

    const homeTotal = (homeScores.p1 || 0) + (homeScores.p2 || 0) + (homeScores.p3 || 0) + (homeScores.ot || 0) + (homeScores.so || 0);
    const awayTotal = (awayScores.p1 || 0) + (awayScores.p2 || 0) + (awayScores.p3 || 0) + (awayScores.ot || 0) + (awayScores.so || 0);
    homeRow.push(String(homeTotal));
    awayRow.push(String(awayTotal));

    return renderTable({ headers, data: [homeRow, awayRow] }, '피리어드별 점수');
  };

  // 최근 폼 렌더링 (테이블 형식)
  const renderRecentForm = (): React.ReactElement | null => {
    if (!recentForm || (!recentForm.home && !recentForm.away)) return null;

    // 결과를 한글로 변환
    const getResultLabel = (result: string): string => {
      switch (result.toUpperCase()) {
        case 'W': return '승';
        case 'L': return '패';
        case 'D': return '무';
        case 'OTL': return '연장패';
        case 'SOL': return 'SO패';
        default: return result;
      }
    };

    // 결과에 따른 스타일
    const getResultStyle = (result: string): React.CSSProperties => {
      const base: React.CSSProperties = {
        padding: `${spacing[1]} ${spacing[2]}`,
        borderRadius: '4px',
        fontWeight: typography.fontWeight.semibold,
        fontSize: typography.fontSize.sm,
        textAlign: 'center' as const,
        minWidth: '40px',
        display: 'inline-block',
      };
      switch (result.toUpperCase()) {
        case 'W':
          return { ...base, backgroundColor: colors.status.success, color: colors.white };
        case 'L':
          return { ...base, backgroundColor: colors.status.error, color: colors.white };
        case 'D':
          return { ...base, backgroundColor: colors.gray[400], color: colors.white };
        case 'OTL':
        case 'SOL':
          return { ...base, backgroundColor: colors.status.warning, color: colors.white };
        default:
          return { ...base, backgroundColor: colors.gray[200], color: colors.gray[700] };
      }
    };

    const homeResults = recentForm.home || [];
    const awayResults = recentForm.away || [];
    const maxLength = Math.max(homeResults.length, awayResults.length, 5);

    // 테이블 헤더 생성 (최근 1~N 경기)
    const headers = ['팀'];
    for (let i = 1; i <= maxLength; i++) {
      headers.push(`${i}`);
    }
    // 승/패 집계
    headers.push('승');
    headers.push('패');

    // 승/패 카운트
    const countResults = (results: string[]) => {
      let wins = 0, losses = 0;
      results.forEach(r => {
        if (r.toUpperCase() === 'W') wins++;
        else if (['L', 'OTL', 'SOL'].includes(r.toUpperCase())) losses++;
      });
      return { wins, losses };
    };

    const homeCount = countResults(homeResults);
    const awayCount = countResults(awayResults);

    return (
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>최근 폼</h3>
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                {headers.map((header, idx) => (
                  <th key={idx} style={{
                    ...styles.th,
                    textAlign: idx === 0 ? 'left' : 'center',
                    minWidth: idx === 0 ? '120px' : '45px',
                  }}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* 홈팀 */}
              <tr>
                <td style={styles.td}>🏠 {homeTeam}</td>
                {Array.from({ length: maxLength }).map((_, i) => (
                  <td key={i} style={{ ...styles.td, textAlign: 'center' }}>
                    {homeResults[i] ? (
                      <span style={getResultStyle(homeResults[i])}>
                        {getResultLabel(homeResults[i])}
                      </span>
                    ) : '-'}
                  </td>
                ))}
                <td style={{ ...styles.td, textAlign: 'center', fontWeight: typography.fontWeight.semibold, color: colors.status.success }}>
                  {homeCount.wins}
                </td>
                <td style={{ ...styles.td, textAlign: 'center', fontWeight: typography.fontWeight.semibold, color: colors.status.error }}>
                  {homeCount.losses}
                </td>
              </tr>
              {/* 원정팀 */}
              <tr>
                <td style={styles.td}>✈️ {awayTeam}</td>
                {Array.from({ length: maxLength }).map((_, i) => (
                  <td key={i} style={{ ...styles.td, textAlign: 'center' }}>
                    {awayResults[i] ? (
                      <span style={getResultStyle(awayResults[i])}>
                        {getResultLabel(awayResults[i])}
                      </span>
                    ) : '-'}
                  </td>
                ))}
                <td style={{ ...styles.td, textAlign: 'center', fontWeight: typography.fontWeight.semibold, color: colors.status.success }}>
                  {awayCount.wins}
                </td>
                <td style={{ ...styles.td, textAlign: 'center', fontWeight: typography.fontWeight.semibold, color: colors.status.error }}>
                  {awayCount.losses}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // 팀 통계 렌더링
  const renderTeamStats = (): React.ReactElement | null => {
    if (!teamStats) return null;

    const homeStats = teamStats.home || teamStats.team_stats?.home || {};
    const awayStats = teamStats.away || teamStats.team_stats?.away || {};

    if (Object.keys(homeStats).length === 0 && Object.keys(awayStats).length === 0) {
      // tables 형식 확인
      if (teamStats.tables && teamStats.tables.length > 0) {
        return (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>팀 통계</h3>
            {teamStats.tables.map((table: any, idx: number) => (
              <div key={idx}>
                {renderTable(table, table.description)}
              </div>
            ))}
          </div>
        );
      }
      return null;
    }

    const allKeys = Array.from(new Set([...Object.keys(homeStats), ...Object.keys(awayStats)]));
    const tableData = {
      headers: ['항목', homeTeam, awayTeam],
      data: allKeys.map(key => [key, String(homeStats[key] ?? '-'), String(awayStats[key] ?? '-')])
    };

    return (
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>팀 통계</h3>
        {renderTable(tableData)}
      </div>
    );
  };

  // 상대전적 렌더링
  const renderH2H = (): React.ReactElement | null => {
    if (!headToHead || ((!headToHead.records || headToHead.records.length === 0) && (!headToHead.stats || headToHead.stats.length === 0))) {
      return null;
    }

    // records가 테이블 형식인지 확인 (description과 data를 가진 객체 배열)
    const isTableFormat = headToHead.records &&
      headToHead.records.length > 0 &&
      headToHead.records[0]?.data;

    return (
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>상대전적</h3>

        {/* 통계 */}
        {headToHead.stats && headToHead.stats.length > 0 && (
          <div style={styles.subsection}>
            {headToHead.stats.map((stat: any, idx: number) => (
              <div key={idx} style={styles.h2hStatRow}>
                <span style={styles.h2hValue}>{stat.home_value}</span>
                <span style={styles.h2hLabel}>{stat.label}</span>
                <span style={styles.h2hValue}>{stat.away_value}</span>
              </div>
            ))}
          </div>
        )}

        {/* 최근 경기 기록 - 테이블 형식 처리 */}
        {isTableFormat && headToHead.records.map((tableRecord: any, tableIdx: number) => (
          <div key={tableIdx} style={styles.subsection}>
            {tableRecord.description && (
              <h4 style={styles.subsectionTitle}>{tableRecord.description}</h4>
            )}
            {tableRecord.data && Array.isArray(tableRecord.data) && tableRecord.data.length > 1 && (
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      {tableRecord.data[0].map((header: string, idx: number) => (
                        <th key={idx} style={styles.th}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableRecord.data.slice(1, 6).map((row: any[], rowIdx: number) => (
                      <tr key={rowIdx}>
                        {row.map((cell, cellIdx) => (
                          <td key={cellIdx} style={styles.td}>{cell || '-'}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}

        {/* 최근 경기 기록 - 플랫 형식 처리 */}
        {!isTableFormat && headToHead.records && headToHead.records.length > 0 && (
          <div style={styles.subsection}>
            <h4 style={styles.subsectionTitle}>최근 경기</h4>
            {headToHead.records.slice(0, 5).map((record: any, idx: number) => (
              <div key={idx} style={styles.h2hRecord}>
                <span style={styles.h2hDate}>{record.date}</span>
                <span style={styles.h2hMatchup}>
                  {record.home_team} {record.score} {record.away_team}
                </span>
                <span style={styles.h2hCompetition}>{record.competition}</span>
              </div>
            ))}
          </div>
        )}

        {/* 요약 */}
        {headToHead.summary && (
          <div style={styles.h2hSummary}>
            <span>홈 승: {headToHead.summary.home_wins || 0}</span>
            <span>무승부: {headToHead.summary.draws || 0}</span>
            <span>원정 승: {headToHead.summary.away_wins || 0}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <h2 style={styles.matchTitle}>{homeTeam} vs {awayTeam}</h2>
        {(dateTime || venue) && (
          <p style={styles.matchInfo}>
            {dateTime && <span>{dateTime}</span>}
            {dateTime && venue && <span> | </span>}
            {venue && <span>{venue}</span>}
          </p>
        )}
      </div>

      {/* 팀 선택 탭 */}
      <div style={styles.teamTabs}>
        <button
          style={selectedTeam === 'home' ? styles.teamTabActive : styles.teamTab}
          onClick={() => setSelectedTeam('home')}
        >
          🏠 {homeTeam}
        </button>
        <button
          style={selectedTeam === 'away' ? styles.teamTabActive : styles.teamTab}
          onClick={() => setSelectedTeam('away')}
        >
          ✈️ {awayTeam}
        </button>
      </div>

      {/* 콘텐츠 */}
      <div style={styles.content}>
        {/* 선발 골텐더 */}
        {(startingGoalies.home_goalie || startingGoalies.away_goalie) && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>선발 골텐더</h3>
            {renderGoalieInfo(startingGoalies.home_goalie, homeTeam, '🏠')}
            {renderGoalieInfo(startingGoalies.away_goalie, awayTeam, '✈️')}
          </div>
        )}

        {/* 피리어드별 점수 */}
        {renderPeriodScores()}

        {/* 팀 통계 */}
        {renderTeamStats()}

        {/* 상대전적 */}
        {renderH2H()}

        {/* 최근 폼 */}
        {renderRecentForm()}
      </div>
    </div>
  );
};

// 스타일
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: spacing[4],
    backgroundColor: colors.gray[50],
    borderRadius: '0',
  },
  header: {
    marginBottom: spacing[6],
    textAlign: 'center' as const,
  },
  matchTitle: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.semibold,
    marginBottom: spacing[1],
  },
  matchInfo: {
    fontSize: typography.fontSize.base,
    color: colors.gray[600],
  },
  teamTabs: {
    display: 'flex',
    marginBottom: spacing[6],
    gap: spacing[2],
  },
  teamTab: {
    flex: 1,
    padding: spacing[2],
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '0',
    backgroundColor: colors.white,
    cursor: 'pointer',
    fontSize: typography.fontSize.base,
  },
  teamTabActive: {
    flex: 1,
    padding: spacing[2],
    border: 'none',
    borderRadius: '0',
    backgroundColor: colors.black,
    color: colors.white,
    cursor: 'pointer',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
  },
  content: {
    maxHeight: '60vh',
    overflowY: 'auto' as const,
  },
  section: {
    marginBottom: spacing[6],
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    marginBottom: spacing[4],
    borderBottom: `1px solid ${colors.gray[300]}`,
    paddingBottom: spacing[1],
  },
  subsection: {
    marginBottom: spacing[4],
  },
  subsectionTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    marginBottom: spacing[2],
  },
  goalieCard: {
    backgroundColor: colors.white,
    padding: spacing[4],
    borderRadius: '0',
    marginBottom: spacing[2],
    border: `1px solid ${colors.gray[300]}`,
  },
  goalieHeader: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    marginBottom: spacing[2],
  },
  goalieStats: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: spacing[4],
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center' as const,
    minWidth: '60px',
  },
  statLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.gray[600],
  },
  statValue: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
  },
  tableContainer: {
    marginBottom: spacing[4],
  },
  tableTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    marginBottom: spacing[1],
  },
  tableWrapper: {
    overflowX: 'auto' as const,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: typography.fontSize.base,
  },
  th: {
    padding: spacing[2],
    textAlign: 'left' as const,
    borderBottom: `2px solid ${colors.gray[300]}`,
    backgroundColor: colors.gray[100],
    fontWeight: typography.fontWeight.semibold,
    whiteSpace: 'nowrap' as const,
  },
  td: {
    padding: spacing[2],
    borderBottom: `1px solid ${colors.gray[200]}`,
    whiteSpace: 'nowrap' as const,
  },
  formRow: {
    display: 'flex',
    alignItems: 'center' as const,
    marginBottom: spacing[2],
    gap: spacing[2],
  },
  formTeam: {
    fontSize: typography.fontSize.base,
    minWidth: '120px',
  },
  formResults: {
    display: 'flex',
    gap: spacing[1],
  },
  formItem: {
    fontSize: '1.2em',
  },
  h2hStatRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center' as const,
    padding: `${spacing[1]} ${spacing[2]}`,
    backgroundColor: colors.white,
    marginBottom: spacing[1],
    borderRadius: '0',
  },
  h2hLabel: {
    fontSize: typography.fontSize.base,
    color: colors.gray[600],
  },
  h2hValue: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    minWidth: '40px',
    textAlign: 'center' as const,
  },
  h2hRecord: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center' as const,
    padding: spacing[2],
    backgroundColor: colors.white,
    marginBottom: spacing[1],
    borderRadius: '0',
    flexWrap: 'wrap' as const,
    gap: spacing[1],
  },
  h2hDate: {
    fontSize: typography.fontSize.sm,
    color: colors.gray[600],
    minWidth: '80px',
  },
  h2hMatchup: {
    fontSize: typography.fontSize.base,
    flex: 1,
    textAlign: 'center' as const,
  },
  h2hCompetition: {
    fontSize: typography.fontSize.sm,
    color: colors.gray[600],
  },
  h2hSummary: {
    display: 'flex',
    justifyContent: 'space-around',
    padding: spacing[4],
    backgroundColor: colors.gray[100],
    borderRadius: '0',
    marginTop: spacing[2],
  },
};

export default HockeyDataDisplay;
